import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadChatImage } from '../api/chat';
import type { ComingSoonProduct, ImageRef, ProductCatalogEntry } from '../types/api';
import { Icon } from '../ui';
import styles from './InputBar.module.css';

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

interface StagedImage {
  id: string;
  name: string;
  dataUrl: string;
  status: 'uploading' | 'done' | 'failed';
  imageRef?: ImageRef;
}

interface Props {
  sessionId: string | null;
  onSend: (query: string, product: string, attachments: ImageRef[], sessionId: string) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
  placeholder?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  attachmentsEnabled?: boolean;
  products: ProductCatalogEntry[];
  comingSoon: ComingSoonProduct[];
  catalogLoading: boolean;
  selectedSlug: string | null;
  onSelectProduct: (slug: string, version: string) => void;
  selectedVersion: string | null;
  /** Product slug to use once the conversation has messages; overrides the local selection. */
  lockedProduct?: string;
  /** The docs version the conversation is pinned to, shown once it has messages. */
  lockedVersion?: string;
  productLocked?: boolean;
}

const MAX_HEIGHT = 144;
// Height threshold (~3 lines: 3*22px line height + 12px padding ≈ 78px; trigger slightly below)
const THREE_LINE_THRESHOLD = 62;
// Non-textarea chrome height (~111px) + desired top gap (~230px) = 341
const EXPAND_CHROME_HEIGHT = 341;

export default function InputBar({
  sessionId,
  onSend,
  onStop,
  isStreaming = false,
  disabled = false,
  placeholder = 'Ask a follow-up question...',
  scrollContainerRef,
  attachmentsEnabled = true,
  products,
  comingSoon,
  catalogLoading,
  selectedSlug,
  onSelectProduct,
  selectedVersion,
  lockedProduct,
  lockedVersion,
  productLocked = false,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const draftSessionIdRef = useRef<string | null>(null);
  const productMenuRef = useRef<HTMLDivElement>(null);
  const productTriggerRef = useRef<HTMLButtonElement>(null);
  const [showExpandBtn, setShowExpandBtn] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [hasText, setHasText] = useState(false);
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stagedCountRef = useRef(0);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  const showNotice = useCallback((message: string) => {
    setNotice(message);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4000);
  }, []);

  const getSessionId = useCallback(() => {
    if (sessionId) return sessionId;
    if (!draftSessionIdRef.current) draftSessionIdRef.current = crypto.randomUUID();
    return draftSessionIdRef.current;
  }, [sessionId]);

  const removeStagedImage = useCallback((id: string) => {
    setStagedImages((prev) => prev.filter((img) => img.id !== id));
    stagedCountRef.current = Math.max(0, stagedCountRef.current - 1);
  }, []);

  const uploadStagedImage = useCallback(
    async (id: string, file: File, dataUrl: string) => {
      try {
        const base64Data = dataUrl.slice(dataUrl.indexOf(',') + 1);
        const result = await uploadChatImage(getSessionId(), file.name, base64Data);
        setStagedImages((prev) =>
          prev.map((img) =>
            img.id === id ? { ...img, status: 'done', imageRef: result.image_ref } : img,
          ),
        );
      } catch {
        removeStagedImage(id);
        showNotice("Couldn't upload this image. You can try again or send without it.");
      }
    },
    [getSessionId, removeStagedImage, showNotice],
  );

  const addStagedImage = useCallback(
    (file: File, dataUrl: string) => {
      const id = crypto.randomUUID();
      setStagedImages((prev) => [...prev, { id, name: file.name, dataUrl, status: 'uploading' }]);
      void uploadStagedImage(id, file, dataUrl);
    },
    [uploadStagedImage],
  );

  const handleFiles = useCallback(
    (files: File[]) => {
      if (!attachmentsEnabled) return;
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      for (const file of imageFiles) {
        if (stagedCountRef.current >= MAX_ATTACHMENTS) {
          showNotice('Maximum 5 images per message');
          break;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          showNotice('Image too large (max 8MB)');
          continue;
        }
        stagedCountRef.current += 1;
        const reader = new FileReader();
        reader.onload = () => addStagedImage(file, reader.result as string);
        reader.readAsDataURL(file);
      }
    },
    [addStagedImage, showNotice, attachmentsEnabled],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) handleFiles(Array.from(e.target.files));
      e.target.value = '';
    },
    [handleFiles],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        handleFiles(files);
      }
    },
    [handleFiles],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled) return;
      e.preventDefault();
      setIsDragOver(true);
    },
    [attachmentsEnabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(Array.from(e.dataTransfer.files));
    },
    [handleFiles],
  );

  const selectProductVersion = useCallback(
    (slug: string, version: string) => {
      onSelectProduct(slug, version);
      setProductMenuOpen(false);
    },
    [onSelectProduct],
  );

  const activeSlug = productLocked ? (lockedProduct ?? null) : selectedSlug;
  const activeProduct = products.find((p) => p.slug === activeSlug);
  const activeVersion = productLocked ? (lockedVersion ?? null) : selectedVersion;
  // A picker with nothing to pick is noise, and it also reads as "you could have
  // chosen something else" when the caller's feature flags leave them one product
  // in one version. Coming-soon teasers live inside this menu, so they are only shown
  // alongside a real choice; reinstating them for such a caller is a deliberate act.
  const showProductPicker =
    products.length > 1 || (products.length === 1 && products[0].versions.length > 1);
  const productLabel = productLocked
    ? (activeProduct?.display_name ?? lockedProduct ?? 'Select product')
    : catalogLoading
      ? 'Loading products...'
      : (activeProduct?.display_name ?? 'Select product');
  // Sessions from before versions were stored have none, and then only the product shows.
  const triggerLabel = activeVersion ? `${productLabel} ${activeVersion}` : productLabel;

  // The product whose version panel is open beside the menu. Opens on hover, and on
  // click for touch and keyboard, and resets whenever the menu closes.
  const [versionPanelSlug, setVersionPanelSlug] = useState<string | null>(null);
  useEffect(() => {
    if (!productMenuOpen) setVersionPanelSlug(null);
  }, [productMenuOpen]);

  useEffect(() => {
    if (!productMenuOpen) return;
    const handlePointer = (e: MouseEvent) => {
      if (!productMenuRef.current?.contains(e.target as Node)) {
        setProductMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setProductMenuOpen(false);
        productTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [productMenuOpen]);

  const autoResize = useCallback(() => {
    const ta = taRef.current;
    if (!ta || isExpanded) return;
    ta.style.height = 'auto';
    const scrollH = ta.scrollHeight;
    ta.style.height = `${Math.min(scrollH, MAX_HEIGHT)}px`;
    ta.style.overflowY = scrollH > MAX_HEIGHT ? 'auto' : 'hidden';
    setShowExpandBtn(scrollH >= THREE_LINE_THRESHOLD);
  }, [isExpanded]);

  const handleInput = useCallback(() => {
    autoResize();
    setHasText((taRef.current?.value.trim().length ?? 0) > 0);
  }, [autoResize]);

  const expand = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    const containerH = scrollContainerRef?.current?.clientHeight ?? window.innerHeight;
    const targetH = Math.max(150, containerH - EXPAND_CHROME_HEIGHT);
    ta.style.transition = 'height 300ms cubic-bezier(0.22, 1, 0.36, 1)';
    ta.style.maxHeight = `${targetH}px`;
    ta.style.height = `${targetH}px`;
    ta.style.overflowY = 'auto';
    setIsExpanded(true);
    setTimeout(() => {
      if (taRef.current) taRef.current.style.transition = '';
    }, 300);
  }, [scrollContainerRef]);

  const collapse = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;

    // 1. Record the current (expanded) visual height
    const currentH = ta.clientHeight;

    // 2. Measure true content height via the height:1px trick.
    //    With height forced to 1px, min-height:38px kicks in as the floor.
    //    Any content taller than 38px overflows → scrollHeight = actual content height.
    //    We deliberately do NOT clear ta.style.maxHeight here because that would
    //    let the CSS max-height:190px clamp scrollHeight to 190px, giving wrong data.
    ta.style.height = '1px';
    const rawScrollH = ta.scrollHeight;

    // 3. Restore to expanded height — still in the same synchronous JS tick,
    //    so the browser has not painted the 1px state yet.
    ta.style.height = `${currentH}px`;

    // 4. Force a layout flush so the browser commits height=currentH as the
    //    "from" state for the upcoming CSS transition.
    void ta.getBoundingClientRect();

    // 5. Animate from expandedH → targetH (no maxHeight change during animation)
    const targetH = Math.min(rawScrollH, MAX_HEIGHT);
    ta.style.transition = 'height 200ms cubic-bezier(0.4, 0, 1, 1)';
    ta.style.height = `${targetH}px`;
    ta.style.overflowY = rawScrollH > MAX_HEIGHT ? 'auto' : 'hidden';

    // 6. After the animation finishes, clean up inline styles and flip React state.
    //    Deferring setIsExpanded(false) keeps .barExpanded / .areaExpanded active
    //    throughout the collapse so the frosted-glass effect doesn't disappear mid-flight.
    setTimeout(() => {
      const t = taRef.current;
      if (!t) return;
      t.style.transition = '';
      t.style.maxHeight = ''; // let CSS max-height: 144px take over
      t.style.height = 'auto';
      const h = t.scrollHeight;
      t.style.height = `${Math.min(h, MAX_HEIGHT)}px`;
      t.style.overflowY = h > MAX_HEIGHT ? 'auto' : 'hidden';
      setShowExpandBtn(h >= THREE_LINE_THRESHOLD);
      setIsExpanded(false);
    }, 200);
  }, []);

  const submit = useCallback(() => {
    const ta = taRef.current;
    if (!ta || !activeSlug || disabled) return;
    const query = ta.value.trim();
    const hasUploading = stagedImages.some((img) => img.status === 'uploading');
    if ((!query && stagedImages.length === 0) || isStreaming || hasUploading) return;
    ta.value = '';
    // Instant reset when submitting (no animation needed)
    if (isExpanded) {
      ta.style.maxHeight = '';
      setIsExpanded(false);
    }
    ta.style.height = 'auto';
    ta.style.overflowY = 'hidden';
    setShowExpandBtn(false);
    setHasText(false);
    const attachments: ImageRef[] = stagedImages
      .filter((img) => img.status === 'done' && img.imageRef)
      .map((img) => img.imageRef as ImageRef);
    setStagedImages([]);
    stagedCountRef.current = 0;
    onSend(query, activeSlug, attachments, getSessionId());
  }, [isStreaming, isExpanded, onSend, stagedImages, getSessionId, activeSlug, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const floating = !scrollContainerRef;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop attach target; file picker/paste cover keyboard use
    <div
      className={`${styles.area} ${floating ? styles.areaFloating : ''} ${isExpanded ? styles.areaExpanded : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleFileInputChange}
      />
      <div
        className={`${styles.bar} ${isExpanded ? styles.barExpanded : ''} ${isDragOver ? styles.dragOver : ''}`}
      >
        {isDragOver && (
          <div className={styles.dragOverlay}>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span>Drop images to attach to your query</span>
          </div>
        )}
        {stagedImages.length > 0 && (
          <div className={styles.attachmentRow}>
            {stagedImages.map((img) => (
              <div key={img.id} className={styles.attachmentChip}>
                <button
                  type="button"
                  className={styles.attachmentThumb}
                  style={{ backgroundImage: `url("${img.dataUrl}")` }}
                  onClick={() => setLightboxUrl(img.dataUrl)}
                  aria-label={`Preview ${img.name}`}
                />
                <span className={styles.attachmentName}>{img.name}</span>
                <button
                  type="button"
                  className={styles.removeAttachment}
                  onClick={() => removeStagedImage(img.id)}
                  aria-label={`Remove ${img.name}`}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={styles.barRow}>
          {attachmentsEnabled && (
            <button
              type="button"
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming}
              title="Attach image"
              aria-label="Attach image"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
          )}
          <textarea
            ref={taRef}
            className={styles.textarea}
            placeholder={placeholder}
            rows={1}
            // biome-ignore lint/a11y/noAutofocus: chat input is the primary control on mount; user-initiated context
            autoFocus
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
          {showExpandBtn && (
            <button
              type="button"
              className={`${styles.expandBtn} ${isExpanded ? styles.expandBtnActive : ''}`}
              onClick={isExpanded ? collapse : expand}
              title={isExpanded ? 'Collapse' : 'Expand'}
              tabIndex={-1}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isExpanded ? (
                  <polyline points="3 5 8 10 13 5" />
                ) : (
                  <polyline points="3 11 8 6 13 11" />
                )}
              </svg>
            </button>
          )}
          {showProductPicker && (
            <div className={styles.productSelector} ref={productMenuRef}>
              <button
                ref={productTriggerRef}
                type="button"
                className={`${styles.productTrigger} ${productMenuOpen ? styles.productTriggerOpen : ''}`}
                onClick={() => setProductMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={productMenuOpen}
                disabled={productLocked}
              >
                {triggerLabel}
                {!productLocked && (
                  <svg
                    className={`${styles.productChevron} ${productMenuOpen ? styles.productChevronOpen : ''}`}
                    aria-hidden="true"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 4.5 6 7.5 9 4.5" />
                  </svg>
                )}
              </button>
              {productMenuOpen && !productLocked && (
                <div className={styles.productMenu} role="menu">
                  {products.map((p) => {
                    const selected = p.slug === selectedSlug;
                    const panelOpen = versionPanelSlug === p.slug;
                    return (
                      // The row and its version panel share one hover area, so moving the
                      // pointer from the row into the panel keeps the panel open.
                      <div
                        key={p.slug}
                        role="none"
                        className={styles.productMenuRow}
                        onMouseEnter={() => setVersionPanelSlug(p.slug)}
                        onMouseLeave={() => setVersionPanelSlug(null)}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          aria-haspopup="menu"
                          aria-expanded={panelOpen}
                          className={`${styles.productMenuItem} ${panelOpen ? styles.productMenuItemActive : ''}`}
                          onClick={() => setVersionPanelSlug(panelOpen ? null : p.slug)}
                        >
                          {/* A bare tick, not a checkbox: without a box it reads as "the current
                            one" rather than promising a multi-select this app does not have. */}
                          <span className={styles.productMenuTick}>
                            {selected && <Icon name="check" size={14} />}
                          </span>
                          <span className={styles.productMenuLabel}>{p.display_name}</span>
                          <span className={styles.productMenuArrow}>
                            <Icon name="chevron-right" size={12} />
                          </span>
                        </button>
                        {panelOpen && (
                          <div
                            className={styles.versionPanel}
                            role="menu"
                            aria-label={`${p.display_name} documentation version`}
                          >
                            {p.versions.map((v, i) => {
                              const current = selected && v === selectedVersion;
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  role="menuitemradio"
                                  aria-checked={current}
                                  className={styles.productMenuItem}
                                  onClick={() => selectProductVersion(p.slug, v)}
                                >
                                  <span className={styles.productMenuTick}>
                                    {current && <Icon name="check" size={14} />}
                                  </span>
                                  <span className={styles.productMenuLabel}>{v}</span>
                                  {i === 0 && p.versions.length > 1 && (
                                    <span className={styles.productMenuMeta}>Latest</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {comingSoon.map((p) => (
                    <div key={p.slug} className={styles.productMenuItemDisabled}>
                      <span className={styles.productMenuTick} />
                      <span className={styles.productMenuLabel}>{p.display_name}</span>
                      <span className={styles.productMenuMeta}>Coming soon</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isStreaming ? (
            <button
              type="button"
              className={styles.stopBtn}
              onClick={onStop}
              title="Stop generating"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className={styles.sendBtn}
              onClick={submit}
              disabled={
                disabled ||
                !activeSlug ||
                (!hasText && stagedImages.length === 0) ||
                stagedImages.some((img) => img.status === 'uploading')
              }
              title="Send"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {notice && <p className={styles.attachmentNotice}>{notice}</p>}
      {/*
       * Not switchable by a caller, and it used to be: the welcome screen passed
       * showDisclaimer={false}, so the one screen where somebody types their very
       * first question was the one screen that told them nothing.
       *
       * Two duties, and only these two, because the space is small print under a text
       * box. That somebody is talking to an AI has to be said at the interaction, and
       * asking them to leave client specifics out only works where the typing happens.
       * That their text is stored is an information duty instead, so it belongs in the
       * privacy notice, which has room to say it properly.
       */}
      <p className={styles.disclaimer}>
        Docs AI is an AI assistant, still in beta and can be wrong. Please double-check the
        responses.
        <br />
        Please keep project and client specifics out of what you type.
      </p>
      {lightboxUrl && (
        <button
          type="button"
          className={styles.lightbox}
          onClick={() => setLightboxUrl(null)}
          aria-label="Close preview"
        >
          <img src={lightboxUrl} alt="Enlarged screenshot" />
        </button>
      )}
    </div>
  );
}
